import { initializeApp, getApps } from "firebase/app";
import {
  ref,
  getDatabase,
  onDisconnect,
  onChildAdded,
  get as nativeGet,
  push as nativePush,
  update as nativeUpdate,
  remove as nativeRemove,
  onValue as nativeOnValue,
  off as nativeOff,
  set as nativeSet,
} from "firebase/database";
import {
  browserLocalPersistence,
  getAuth,
  inMemoryPersistence,
  setPersistence,
  signInWithCustomToken,
} from "firebase/auth";
import CONFIG from "@bd-core/config";
import bdjs from "@bd-core/bdjs";
import { store } from "@bd-stores/store";
import { setPlayers } from "@bd-stores/game-progress";
import {
  setFirebaseInitialized,
  setCurrentPageId,
} from "@bd-stores/user-progress";
import { setBuildingBlocksAnswer } from "@bd-stores/game-data";
import bdapi from "@bd-core/bd-api";

const fb = (() => {
  const firebaseEnvironment = CONFIG.firebase?.environment ?? "local";
  const setupPath = `${firebaseEnvironment}/setup`;
  const buildingBlocksSetupPath = `${setupPath}/building_blocks`;

  const teamPath = `${firebaseEnvironment}/teams/${CONFIG.firebase?.team_code?.toUpperCase?.()}`;
  const playersPath = `${teamPath}/players`;
  const chatsPath = `${teamPath}/chats`;
  const buildingBlocksPath = `${teamPath}/building_blocks`;
  const teamAttributesPath = `${teamPath}/attributes`;

  /**
   * Get particular player path inside current team
   * @param {number} userId
   */
  const getPlayerPath = (userId) => `${playersPath}/${userId}`;
  const getOnlinePath = (playerPath) => `${playerPath}/isOnline`;

  const currentPlayerPath = getPlayerPath(CONFIG.user?.id);
  const onlinePath = getOnlinePath(currentPlayerPath);

  /**
   * @param {string} path
   */
  const getRefPath = (path) => ref(getDatabase(), path);
  const getRefPlayer = () => getRefPath(currentPlayerPath);
  const getRefPlayers = () => getRefPath(playersPath);
  const getRefPlayerOnline = () => getRefPath(onlinePath);
  const getRefTeam = () => getRefPath(teamPath);
  const getRefTeamAttr = () => getRefPath(teamAttributesPath);
  const getRefBuildingBlocks = () => getRefPath(buildingBlocksPath);
  const getRefChats = () => getRefPath(chatsPath);
  const getRefConnected = () => getRefPath(".info/connected");

  /**
   * Get new firebase token via ajax request
   */
  const getNewFirebaseToken = async () => {
    const [response, requestErr] = bdjs.goAsync(
      bdapi.post("game/firebase-token"),
    );

    if (requestErr) {
      return console.error("Failed to get new firebase token");
    }

    return response.data.data.token;
  };

  /**
   * Initialize firebase
   */
  const initFirebase = async () => {
    if (getApps().length === 0) {
      const config = {
        apiKey: CONFIG.firebase.key,
        authDomain: CONFIG.firebase.auth_domain,
        databaseURL: CONFIG.firebase.database_url,
      };

      initializeApp(config);
    }

    const auth = getAuth();
    try {
      await setPersistence(auth, browserLocalPersistence);
    } catch (e) {
      console.warn("Local persistence failed, falling back to in-memory:", e);
      await setPersistence(auth, inMemoryPersistence);
    }

    /**
     * NOTE: IMPORTANT
     * Avoid using promise.catch() for every firebase auth, you should always use
     * try catch block instead.
     * see:
     * https://github.com/firebase/firebase-js-sdk/issues/4242
     * https://github.com/firebase/firebase-js-sdk/issues/1881
     */
    try {
      await signInWithCustomToken(auth, CONFIG.firebase.auth_token);
    } catch (err) {
      console.error("Firebase auth failed, trying to get new token", err);
      // in case error happen, Get new token and retry to initFirebase again
      const newToken = await getNewFirebaseToken();
      CONFIG.firebase.auth_token = newToken;
      return await initFirebase();
    }

    return true;
  };

  /**
   * Initialize game
   */
  const initializeGame = async () => {
    if (!CONFIG.firebase) {
      return;
    }

    bdjs.showLoading();

    const init = await bdjs.goAsync(initFirebase());
    const initError = init[1];

    if (initError) {
      console.error("Error initializing firebase app `initFirebase()`");
      return;
    }

    // Remove isOnline when client has disconnected by closing the app,
    // losing internet, or any other means.
    onDisconnect(getRefPlayerOnline()).remove();

    // Trigger bd__connectedToFirebase when client has connected to firebase
    nativeOnValue(getRefConnected(), (snapshot) => {
      const value = snapshot.val();
      const isConnected = value === true;
      if (!isConnected) return;

      nativeGet(getRefPlayer()).then((snapshot) => {
        const player = snapshot.val();
        const playerId = player?.userId || false;

        if (!playerId) return;

        nativeUpdate(getRefPlayer(), { isOnline: true });
        bdjs.trigger("bd__connectedToFirebase");
      });
    });

    await firebaseListener();
    store.dispatch(setFirebaseInitialized(true));
    return true;
  };

  /**
   * Register all firebase listener
   */
  const firebaseListener = () => {
    const maybePlayersDataChanged = (snapshot) => {
      const value = snapshot.val();
      if (!value) return;

      const players = bdjs.indexedObjectToArray(value);

      store.dispatch(setPlayers(players));
      bdjs.trigger("bd__playersStateChanged", players);

      const userId = CONFIG.user.id;
      const currentPlayer = players.find((p) => p.userId === userId);

      if (currentPlayer && currentPlayer?.currentPageId) {
        store.dispatch(setCurrentPageId(currentPlayer.currentPageId));
      }

      bdjs.trigger("bd__playerStateChanged", currentPlayer);
    };

    const maybeTeamAttributeChanged = (snapshot) => {
      const value = snapshot.val();
      if (!value) return;
      bdjs.trigger("bd__teamAttributeChanged", value);
    };

    const maybeBuildingBlocksDataChanged = (snapshot) => {
      const value = snapshot.val();
      bdjs.trigger("bd__buildingBlocksDataChanged", value);
    };

    const onChatSubmitted = (snapshot, isNew) => {
      var value = snapshot.val();
      if (!value) return;
      bdjs.trigger("bd__onChatSubmmited", { ...value, isNew });
    };

    //* For listen initial state */
    // ----------------------------------------------------------------------------------

    return new Promise((resolve) => {
      nativeOnValue(
        getRefTeam(),
        async (snapshot) => {
          const answersSnapshot = await get(buildingBlocksSetupPath);
          const buildingBlocksAnswers = answersSnapshot.val() || {};
          store.dispatch(setBuildingBlocksAnswer(buildingBlocksAnswers));
          bdjs.trigger("bd__buildingBlocksAnswersReady", buildingBlocksAnswers);

          const value = snapshot.val();
          const players = bdjs.indexedObjectToArray(value.players);

          store.dispatch(setPlayers(players));
          bdjs.trigger("bd__teamInitialState", value);

          nativeOnValue(getRefPlayers(), maybePlayersDataChanged);
          nativeOnValue(getRefTeamAttr(), maybeTeamAttributeChanged);
          nativeOnValue(getRefBuildingBlocks(), maybeBuildingBlocksDataChanged);

          // Store chat key that has been existed before
          const chats = value?.chats || {};
          const chatsKeys = Object.keys(chats);

          onChildAdded(getRefChats(), (snapshot) => {
            // Prevent scroll aspiration on child that has been existed
            // because firebase will trigger this event once for each
            // existing child and then again every time a new child is
            // added
            // see: https://firebase.google.com/docs/database/web/lists-of-data#listen_for_child_events
            const isNewChild = !chatsKeys.includes(snapshot.key);
            onChatSubmitted(snapshot, isNewChild);
          });

          bdjs.hideLoading();
          resolve();
        },
        { onlyOnce: true },
      );
    });
  };

  /* Firebase helper
   * =======================================================================================================================================
   */

  const get = (path) => {
    return nativeGet(getRefPath(path));
  };

  const push = (path, obj) => {
    return nativePush(getRefPath(path), obj);
  };

  const update = (path, obj) => {
    return nativeUpdate(getRefPath(path), obj);
  };

  const set = (path, obj) => {
    return nativeSet(getRefPath(path), obj);
  };

  const remove = (path) => {
    return nativeRemove(getRefPath(path));
  };

  const onValue = (path, callback) => {
    return nativeOnValue(getRefPath(path), callback);
  };

  const offValue = (path, callback) => {
    return nativeOff(getRefPath(path), "value", callback);
  };

  return {
    getPlayerPath,
    getOnlinePath,
    initializeGame,
    get,
    set,
    push,
    update,
    remove,
    onValue,
    offValue,
    teamPath,
    teamAttributesPath,
    playersPath,
    currentPlayerPath,
    onlinePath,
    chatsPath,
    buildingBlocksSetupPath,
    buildingBlocksPath,
  };
})();

export default fb;
